import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useUser } from '../context/UserContext';

const SellerRegistrationForm = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useUser();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [applicationStatus, setApplicationStatus] = useState(null);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    // Store Info
    storeName: '',
    storeAddress: '',
    storeDescription: '',
    storeLogo: null,
    
    // KYC Info
    fullName: '',
    aadhaarNumber: '',
    panNumber: '',
    idProof: null,
    
    // Bank Details
    accountHolderName: '',
    accountNumber: '',
    ifscCode: '',
    upiId: ''
  });

  const API_URL = 'http://localhost:5000/api';

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/account?redirect=seller/register');
      return;
    }
    checkApplicationStatus();
  }, [isAuthenticated, navigate]);

  const checkApplicationStatus = async () => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await axios.get(`${API_URL}/seller/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.hasApplied) {
        setSubmitted(true);
        setApplicationStatus(response.data.application);
      }
    } catch (error) {
      console.error('Error checking status:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    if (type === 'file') {
      setFormData(prev => ({ ...prev, [name]: files[0] }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Store Info
    if (!formData.storeName.trim()) newErrors.storeName = 'Store name is required';
    if (!formData.storeAddress.trim()) newErrors.storeAddress = 'Store address is required';
    
    // KYC Info
    if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required';
    
    if (!formData.aadhaarNumber && !formData.panNumber) {
      newErrors.idProof = 'Please provide either Aadhaar Number or PAN Number';
    }
    
    if (formData.aadhaarNumber && !/^\d{12}$/.test(formData.aadhaarNumber)) {
      newErrors.aadhaarNumber = 'Aadhaar number must be 12 digits';
    }
    
    if (formData.panNumber && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/i.test(formData.panNumber)) {
      newErrors.panNumber = 'Invalid PAN number format (e.g., ABCDE1234F)';
    }
    
    if (!formData.idProof) newErrors.idProof = 'ID proof is required';
    
    // Bank Details
    if (!formData.accountHolderName.trim()) newErrors.accountHolderName = 'Account holder name is required';
    if (!formData.accountNumber.trim()) newErrors.accountNumber = 'Account number is required';
    if (!formData.ifscCode.trim()) newErrors.ifscCode = 'IFSC code is required';
    
    const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/i;
    if (formData.ifscCode && !ifscRegex.test(formData.ifscCode)) {
      newErrors.ifscCode = 'Invalid IFSC code (e.g., SBIN0001234)';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      
      const formDataToSend = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== '') {
          formDataToSend.append(key, formData[key]);
        }
      });
      
      const response = await axios.post(`${API_URL}/seller/apply`, formDataToSend, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.success) {
        setSubmitted(true);
        setApplicationStatus({
          sellerStatus: 'pending',
          appliedAt: new Date().toISOString()
        });
      }
    } catch (error) {
      console.error('Submit error:', error);
      alert(error.response?.data?.message || 'Error submitting application');
    } finally {
      setLoading(false);
    }
  };

  // If already submitted, show status
  if (submitted) {
    return (
      <div className="container container-lg py-60">
        <div className="row justify-content-center">
          <div className="col-lg-6">
            <div className="text-center p-40 border rounded-16 bg-white">
              <div className="mb-24">
                <span className="w-80 h-80 bg-warning-100 text-warning-600 rounded-circle d-flex align-items-center justify-content-center mx-auto">
                  <i className="ph ph-clock fs-1"></i>
                </span>
              </div>
              <h2 className="mb-16">Application Submitted!</h2>
              <p className="text-gray-500 mb-24">
                Your seller application has been received and is pending review.
              </p>
              <div className="bg-light p-24 rounded-12 mb-24">
                <div className="d-flex justify-content-between mb-12">
                  <span className="text-gray-600">Status:</span>
                  <span className="badge bg-warning-600 text-white">Pending Approval</span>
                </div>
                <div className="d-flex justify-content-between">
                  <span className="text-gray-600">Applied on:</span>
                  <span>{new Date(applicationStatus?.appliedAt).toLocaleDateString()}</span>
                </div>
              </div>
              <p className="text-gray-500 mb-32">
                You will be notified via email once your application is reviewed.
              </p>
              <Link to="/" className="btn btn-main py-16 px-40">
                Go to Homepage
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container container-lg py-60">
      <div className="row justify-content-center">
        <div className="col-lg-10">
          <div className="text-center mb-40">
            <h1 className="mb-16">Become a Seller on Paudhawala</h1>
            <p className="text-gray-500">
              Join thousands of sellers and grow your business with India's largest plant marketplace
            </p>
          </div>

{/* User Info - Read Only */}
              <div className="bg-light p-24 rounded-12 mb-32">
                <h6 className="mb-16">Your Account Information</h6>
                <div className="row">
                  <div className="col-md-4 mb-16">
                    <label className="text-sm text-gray-500 d-block">Username</label>
                    <strong>{user?.username}</strong>
                  </div>
                  <div className="col-md-4 mb-16">
                    <label className="text-sm text-gray-500 d-block">Email</label>
                    <strong>{user?.email}</strong>
                    {user?.emailVerified && (
                      <span className="badge bg-success ms-2 text-white" style={{ fontSize: '10px' }}>
                        <i className="ph ph-check-circle"></i> Verified
                      </span>
                    )}
                  </div>
                  <div className="col-md-4 mb-16">
                    <label className="text-sm text-gray-500 d-block">Phone</label>
                    <strong>{user?.phone}</strong>
                  </div>
                </div>
              </div>
              
          <form onSubmit={handleSubmit} className="border rounded-16 p-40 bg-white">
            {/* Store Information Section */}
            <div className="mb-32">
              <h3 className="mb-24 pb-16 border-bottom">Store Information</h3>
              
              <div className="mb-24">
                <label className="form-label fw-medium">
                  Store Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  name="storeName"
                  className={`form-control ${errors.storeName ? 'is-invalid' : ''}`}
                  value={formData.storeName}
                  onChange={handleChange}
                  placeholder="e.g., Green Leaf Nursery"
                />
                {errors.storeName && <div className="invalid-feedback">{errors.storeName}</div>}
              </div>

              <div className="mb-24">
                <label className="form-label fw-medium">
                  Store Address <span className="text-danger">*</span>
                </label>
                <textarea
                  name="storeAddress"
                  className={`form-control ${errors.storeAddress ? 'is-invalid' : ''}`}
                  value={formData.storeAddress}
                  onChange={handleChange}
                  rows="3"
                  placeholder="Complete store address with city, state, pincode"
                />
                {errors.storeAddress && <div className="invalid-feedback">{errors.storeAddress}</div>}
              </div>

              <div className="mb-24">
                <label className="form-label fw-medium">Store Description</label>
                <textarea
                  name="storeDescription"
                  className="form-control"
                  value={formData.storeDescription}
                  onChange={handleChange}
                  rows="4"
                  placeholder="Tell customers about your store, products, and services"
                />
              </div>

              <div className="mb-24">
                <label className="form-label fw-medium">Store Logo</label>
                <input
                  type="file"
                  name="storeLogo"
                  className="form-control"
                  onChange={handleChange}
                  accept="image/*"
                />
                <small className="text-gray-400">Recommended: Square image, max 2MB</small>
              </div>
            </div>

            {/* KYC Information Section */}
            <div className="mb-32">
              <h3 className="mb-24 pb-16 border-bottom">KYC Information</h3>
              
              <div className="mb-24">
                <label className="form-label fw-medium">
                  Full Name (as per ID proof) <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  name="fullName"
                  className={`form-control ${errors.fullName ? 'is-invalid' : ''}`}
                  value={formData.fullName}
                  onChange={handleChange}
                  placeholder="Full name as per Aadhaar/PAN"
                />
                {errors.fullName && <div className="invalid-feedback">{errors.fullName}</div>}
              </div>

              <div className="row">
                <div className="col-md-6 mb-24">
                  <label className="form-label fw-medium">Aadhaar Number</label>
                  <input
                    type="text"
                    name="aadhaarNumber"
                    className={`form-control ${errors.aadhaarNumber ? 'is-invalid' : ''}`}
                    value={formData.aadhaarNumber}
                    onChange={handleChange}
                    placeholder="12-digit Aadhaar number"
                    maxLength="12"
                  />
                  {errors.aadhaarNumber && <div className="invalid-feedback">{errors.aadhaarNumber}</div>}
                </div>

                <div className="col-md-6 mb-24">
                  <label className="form-label fw-medium">PAN Number</label>
                  <input
                    type="text"
                    name="panNumber"
                    className={`form-control ${errors.panNumber ? 'is-invalid' : ''}`}
                    value={formData.panNumber}
                    onChange={handleChange}
                    placeholder="e.g., ABCDE1234F"
                    maxLength="10"
                  />
                  {errors.panNumber && <div className="invalid-feedback">{errors.panNumber}</div>}
                </div>
              </div>

              {errors.idProof && <div className="text-danger mb-24">{errors.idProof}</div>}

              <div className="mb-24">
                <label className="form-label fw-medium">
                  ID Proof <span className="text-danger">*</span>
                </label>
                <input
                  type="file"
                  name="idProof"
                  className={`form-control ${errors.idProof ? 'is-invalid' : ''}`}
                  onChange={handleChange}
                  accept="image/*,.pdf"
                />
                <small className="text-gray-400">Upload Aadhaar card, PAN card, or Voter ID (max 5MB)</small>
                {errors.idProof && <div className="invalid-feedback d-block">{errors.idProof}</div>}
              </div>
            </div>

            {/* Bank Details Section */}
            <div className="mb-32">
              <h3 className="mb-24 pb-16 border-bottom">Bank Details</h3>
              
              <div className="mb-24">
                <label className="form-label fw-medium">
                  Account Holder Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  name="accountHolderName"
                  className={`form-control ${errors.accountHolderName ? 'is-invalid' : ''}`}
                  value={formData.accountHolderName}
                  onChange={handleChange}
                  placeholder="Name as per bank account"
                />
                {errors.accountHolderName && <div className="invalid-feedback">{errors.accountHolderName}</div>}
              </div>

              <div className="row">
                <div className="col-md-6 mb-24">
                  <label className="form-label fw-medium">
                    Account Number <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    name="accountNumber"
                    className={`form-control ${errors.accountNumber ? 'is-invalid' : ''}`}
                    value={formData.accountNumber}
                    onChange={handleChange}
                    placeholder="Bank account number"
                  />
                  {errors.accountNumber && <div className="invalid-feedback">{errors.accountNumber}</div>}
                </div>

                <div className="col-md-6 mb-24">
                  <label className="form-label fw-medium">
                    IFSC Code <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    name="ifscCode"
                    className={`form-control ${errors.ifscCode ? 'is-invalid' : ''}`}
                    value={formData.ifscCode}
                    onChange={handleChange}
                    placeholder="e.g., SBIN0001234"
                    maxLength="11"
                  />
                  {errors.ifscCode && <div className="invalid-feedback">{errors.ifscCode}</div>}
                </div>
              </div>

              <div className="mb-24">
                <label className="form-label fw-medium">UPI ID (Optional)</label>
                <input
                  type="text"
                  name="upiId"
                  className="form-control"
                  value={formData.upiId}
                  onChange={handleChange}
                  placeholder="yourname@okhdfcbank"
                />
                <small className="text-gray-400">For faster settlements</small>
              </div>
            </div>

            {/* Submit Button */}
            <div className="mt-32">
              <button
                type="submit"
                className="btn btn-main w-100 py-16"
                disabled={loading}
              >
                {loading ? 'Submitting...' : 'Submit Seller Application'}
              </button>
            </div>

            {/* Info Box */}
            <div className="bg-light p-24 rounded-12 mt-32">
              <div className="d-flex align-items-start gap-16">
                <i className="ph ph-info text-main-600 fs-4"></i>
                <div>
                  <h6 className="mb-8">What happens next?</h6>
                  <p className="text-gray-500 mb-0">
                    Your application will be reviewed by our team. You'll receive an email notification once your seller account is approved. 
                    This usually takes 2-3 business days.
                  </p>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SellerRegistrationForm;